import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Swal from "sweetalert2";
import "./rules.css";
import { fetchThemeData } from "../../themeSlice";
import { selectAdminToken } from "../../sessionSlice";
import { useNavigate } from "react-router-dom";
import { updateThemeData } from "../../functions/updateThemeData";

/** Normalize rules from API (array, JSON string, or PHP-serialized-ish string). */
function normalizeRules(val) {
  if (Array.isArray(val)) {
    const arr = val.map((r) => String(r).trim()).filter(Boolean);
    return arr.length ? arr : [""];
  }
  if (typeof val === "string") {
    try {
      const p = JSON.parse(val);
      if (Array.isArray(p)) {
        const arr = p.map((r) => String(r).trim()).filter(Boolean);
        return arr.length ? arr : [""];
      }
    } catch {
      /* fall through */
    }
    const regex = /s:\d+:"([^"]*)";/g;
    const out = [];
    let m;
    while ((m = regex.exec(val)) !== null) {
      if (m[1].trim()) out.push(m[1].trim());
    }
    if (out.length) return out;
  }
  return [""];
}

function countWords(data) {
  const w = data?.words;
  if (Array.isArray(w)) return w.length;
  if (typeof w === "string") {
    try {
      const p = JSON.parse(w);
      if (Array.isArray(p)) return p.length;
    } catch {
      /* fall through */
    }
    const regex = /s:\d+:"([^"]*)";/g;
    let n = 0;
    while (regex.exec(w) !== null) n += 1;
    return n;
  }
  return 0;
}

function safeHex(v, fallback) {
  if (typeof v !== "string" || !/^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(v.trim())) {
    return fallback;
  }
  return v.trim();
}

const Rules = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const adminToken = useSelector(selectAdminToken);
  const { currentTheme, data } = useSelector((state) => state.theme);

  const [landingTitle, setLandingTitle] = useState("");
  const [rules, setRules] = useState([""]);
  const [points, setPoints] = useState("5");
  const [submitShow, setSubmitShow] = useState("5");
  const [titleTextColor, setTitleTextColor] = useState("#000000");
  const [landingPageTitleColor, setLandingPageTitleColor] = useState("#000000");
  const [btnColor, setBtnColor] = useState("#ababab");
  const [btnBgcolor, setBtnBgcolor] = useState("#c7cff5");
  const [gridColor, setGridColor] = useState("#2196f3");
  const [beginSubmitProgressBgColor, setBeginSubmitProgressBgColor] = useState("#034b82");
  const [gridActiveTextColor, setGridActiveTextColor] = useState("#ffffff");
  const [wordButtonTextColor, setWordButtonTextColor] = useState("#ffffff");
  const [wordButtonBgColor, setWordButtonBgColor] = useState("#034b82");
  const [wordGridBgColor, setWordGridBgColor] = useState("#ffffff");
  const [wordGridTextColor, setWordGridTextColor] = useState("#000000");
  const [rightDiagonal, setRightDiagonal] = useState(true);
  const [saving, setSaving] = useState(false);
  const [togglingDiagonal, setTogglingDiagonal] = useState(false);

  const themeId = currentTheme || data?.themename;

  useEffect(() => {
    if (themeId) dispatch(fetchThemeData({ themeId }));
  }, [dispatch, themeId]);

  useEffect(() => {
    if (!data) return;
    setLandingTitle(
      typeof data.landing_page_title === "string" ? data.landing_page_title : ""
    );
    setRules(normalizeRules(data.rules));
    setPoints(String(data.points != null ? data.points : 5));
    setSubmitShow(String(data.submitShow != null ? data.submitShow : 5));
    setTitleTextColor(safeHex(data.landing_page_title_color, "#000000"));
    setLandingPageTitleColor(safeHex(data.text_color, "#000000"));
    setBtnColor(safeHex(data.btn_color || data.button_color, "#ababab"));
    setBtnBgcolor(
      safeHex(data.btn_bgcolor || data.button_backgroundcolor, "#c7cff5")
    );
    setGridColor(safeHex(data.grid_color, "#2196f3"));
    setBeginSubmitProgressBgColor(
      safeHex(data.begin_submit_progress_bg_color || data.button_backgroundcolor, "#034b82")
    );
    setGridActiveTextColor(safeHex(data.grid_active_text_color, "#ffffff"));
    setWordButtonTextColor(
      safeHex(data.word_button_text_color || data.button_color, "#ffffff")
    );
    setWordButtonBgColor(
      safeHex(data.word_button_bg_color || data.button_backgroundcolor, "#034b82")
    );
    setWordGridBgColor(safeHex(data.word_grid_bg_color, "#ffffff"));
    setWordGridTextColor(safeHex(data.word_grid_text_color, "#000000"));
    setRightDiagonal(
      data.rightDiagonal === "true" || data.rightDiagonal === true
    );
  }, [data]);

  const wordCount = useMemo(() => countWords(data || {}), [data]);

  const handleRuleChange = (idx, val) => {
    if (val.length > 200) {
      Swal.fire(
        "Limit exceeded",
        "Each rule must be 200 characters or less.",
        "warning"
      );
      val = val.slice(0, 200);
    }
    const arr = [...rules];
    arr[idx] = val;
    setRules(arr);
  };

  const handleAddRule = () => {
    if (rules.length >= 6) {
      Swal.fire("Limit", "You can add at most 6 rules.", "warning");
      return;
    }
    setRules([...rules, ""]);
  };

  const handleRemoveRule = (idx) => {
    if (rules.length <= 1) {
      Swal.fire("Required", "At least one rule is required.", "warning");
      return;
    }
    setRules(rules.filter((_, i) => i !== idx));
  };

  const persistDiagonal = async (next) => {
    if (!themeId || !adminToken) return;
    setTogglingDiagonal(true);
    try {
      await updateThemeData({
        payload: {
          data: { rightDiagonal: next ? "true" : "false" },
          currentTheme: themeId,
        },
        token: adminToken,
      });
      setRightDiagonal(next);
      await dispatch(fetchThemeData({ themeId })).unwrap();
    } catch (e) {
      Swal.fire("Error", e?.message || "Could not update diagonal mode.", "error");
    } finally {
      setTogglingDiagonal(false);
    }
  };

  const handleSubmit = async () => {
    const trimmedRules = rules.map((r) => r.trim());
    if (trimmedRules.some((r) => !r)) {
      Swal.fire("Validation", "Please fill all rule fields.", "error");
      return;
    }
    if (trimmedRules.some((r) => r.length > 200)) {
      Swal.fire("Validation", "Rules must be 200 characters or less.", "error");
      return;
    }

    const title = landingTitle;
    const marks = String(points).replace(/\D/g, "");
    const minWords = String(submitShow).replace(/\D/g, "");

    let errorMsg = "";
    if (!/^\d+$/.test(marks) || parseInt(marks, 10) < 1) {
      errorMsg = "Points per word must be a valid number and at least 1.";
    } else if (parseInt(marks, 10) > 100) {
      errorMsg = "Points per word cannot be more than 100.";
    } else if (!/^\d+$/.test(minWords)) {
      errorMsg = "Required words must be a valid number.";
    } else if (parseInt(minWords, 10) > wordCount && wordCount > 0) {
      errorMsg = `Required words cannot be more than total words in the grid (${wordCount}).`;
    } else if (title.length > 150) {
      errorMsg = "Landing title exceeds the maximum of 150 characters.";
    }

    if (errorMsg) {
      Swal.fire("Validation", errorMsg, "error");
      return;
    }

    const payloadData = {
      rules: trimmedRules,
      landing_page_title: title,
      landing_page_title_color: titleTextColor,
      text_color: landingPageTitleColor,
      grid_color: gridColor,
      btn_color: btnColor,
      btn_bgcolor: btnBgcolor,
      button_color: btnColor,
      button_backgroundcolor: btnBgcolor,
      begin_submit_progress_bg_color: beginSubmitProgressBgColor,
      grid_active_text_color: gridActiveTextColor,
      word_button_text_color: wordButtonTextColor,
      word_button_bg_color: wordButtonBgColor,
      word_grid_bg_color: wordGridBgColor,
      word_grid_text_color: wordGridTextColor,
      submitShow: parseInt(minWords, 10),
      points: parseInt(marks, 10),
      rightDiagonal: rightDiagonal ? "true" : "false",
    };

    setSaving(true);
    try {
      await updateThemeData({
        payload: { data: payloadData, currentTheme: themeId },
        token: adminToken,
      });
      await dispatch(fetchThemeData({ themeId })).unwrap();
      Swal.fire("Success", "Data updated.", "success");
      setTimeout(() => navigate("/admin/theme-update"), 600);
    } catch (e) {
      Swal.fire("Error", e?.message || "Failed to save.", "error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <div className="back-button-holder">
        <button
          type="button"
          className="back-button"
          onClick={() => navigate("/admin/")}
        >
          <i className="fa-solid fa-angle-double-left" aria-hidden="true" />{" "}
          BACK
        </button>
      </div>

      <div className="rules-php-layout">
        <div className="rules-php-columns">
          {/* Left: title, rules */}
          <div className="rules-php-card">
            <div className="rules-php-card-body">
              <h4 className="rules-php-card-title">Add Custom Title:</h4>
              <textarea
                id="rules-landing-title"
                className="rules-php-input"
                rows={4}
                maxLength={150}
                value={landingTitle}
                onChange={(e) => setLandingTitle(e.target.value)}
                placeholder="Main game page title"
              />
              <p className="rules-php-char-hint">(maximum 150 characters)</p>
              <p className="rules-php-muted">
                <code>* This title will reflect on the main game page</code>
              </p>

              <h4 className="rules-php-card-title">Add Custom Rules:</h4>
              <div className="rules-repeater">
                {rules.map((rule, i) => (
                  <div className="rules-php-rule-row" key={i}>
                    <textarea
                      className="rules-php-input rules-php-rule-textarea"
                      placeholder="Rules"
                      rows={3}
                      maxLength={200}
                      value={rule}
                      onChange={(e) => handleRuleChange(i, e.target.value)}
                    />
                    <button
                      type="button"
                      className="rules-php-delete-btn"
                      onClick={() => handleRemoveRule(i)}
                      aria-label="Remove rule"
                    >
                      <i className="fa-solid fa-xmark" />
                    </button>
                  </div>
                ))}
                <button
                  type="button"
                  className="rules-php-add-rule"
                  onClick={handleAddRule}
                >
                  <i className="fa-solid fa-plus" /> Add new rule
                </button>
                <p className="rules-php-muted">
                  <code>
                    * Keep 6 rules at the most <br />
                    * At least 1 rule is required <br />* Character limit of each
                    rule is 200
                  </code>
                </p>
              </div>
            </div>
          </div>

          {/* Right: Page content (matches rules.php) */}
          <div className="rules-php-card">
            <div className="rules-php-card-body">
              <h4 className="rules-php-card-title">Page Content</h4>

              <div className="rules-php-form-row">
                <label htmlFor="rules-points" className="rules-php-label">
                  Points per Word
                </label>
                <div className="rules-php-field">
                  <input
                    id="rules-points"
                    type="number"
                    min={1}
                    max={100}
                    className="rules-php-input rules-php-input-narrow"
                    value={points}
                    onChange={(e) => {
                      const v = e.target.value.replace(/\D/g, "");
                      if (!v) {
                        setPoints("");
                        return;
                      }
                      const n = Math.min(100, parseInt(v, 10));
                      setPoints(String(n));
                    }}
                  />
                </div>
              </div>

              <div className="rules-php-form-row">
                <label htmlFor="rules-submit-show" className="rules-php-label">
                  Required Words
                </label>
                <div className="rules-php-field">
                  <input
                    id="rules-submit-show"
                    type="number"
                    min={1}
                    max={10}
                    className="rules-php-input rules-php-input-narrow"
                    value={submitShow}
                    onChange={(e) => {
                      const v = e.target.value.replace(/\D/g, "");
                      if (v.length <= 1) {
                        setSubmitShow(v.replace(/^0/g, "") || "");
                      } else {
                        setSubmitShow(v);
                      }
                    }}
                  />
                </div>
              </div>

              <div className="rules-php-form-row">
                <label htmlFor="rules-title-text-color" className="rules-php-label">
                  Title text color
                </label>
                <div className="rules-php-field">
                  <input
                    id="rules-title-text-color"
                    type="color"
                    className="rules-php-color-input"
                    value={titleTextColor}
                    onChange={(e) => setTitleTextColor(e.target.value)}
                  />
                </div>
              </div>

              <div className="rules-php-form-row">
                <label htmlFor="rules-landing-color" className="rules-php-label">
                  Begin Play/Submit/Progress text Color
                </label>
                <div className="rules-php-field">
                  <input
                    id="rules-landing-color"
                    type="color"
                    className="rules-php-color-input"
                    value={landingPageTitleColor}
                    onChange={(e) => setLandingPageTitleColor(e.target.value)}
                  />
                </div>
              </div>

              <div className="rules-php-form-row">
                <label htmlFor="rules-begin-submit-progress-bg" className="rules-php-label">
                  Begin Play/Submit/Progress Background Color
                </label>
                <div className="rules-php-field">
                  <input
                    id="rules-begin-submit-progress-bg"
                    type="color"
                    className="rules-php-color-input"
                    value={beginSubmitProgressBgColor}
                    onChange={(e) => setBeginSubmitProgressBgColor(e.target.value)}
                  />
                </div>
              </div>

              <div className="rules-php-form-row">
                <label htmlFor="rules-btn-color" className="rules-php-label">
                  Timer/Point/Rules text Color
                </label>
                <div className="rules-php-field">
                  <input
                    id="rules-btn-color"
                    type="color"
                    className="rules-php-color-input"
                    value={btnColor}
                    onChange={(e) => setBtnColor(e.target.value)}
                  />
                </div>
              </div>

              <div className="rules-php-form-row">
                <label htmlFor="rules-btn-bg" className="rules-php-label">
                  Timer/Point/Rules Background Color
                </label>
                <div className="rules-php-field">
                  <input
                    id="rules-btn-bg"
                    type="color"
                    className="rules-php-color-input"
                    value={btnBgcolor}
                    onChange={(e) => setBtnBgcolor(e.target.value)}
                  />
                </div>
              </div>

              <div className="rules-php-form-row">
                <label htmlFor="rules-grid-color" className="rules-php-label">
                  Grid Word Select/Active Background Color
                </label>
                <div className="rules-php-field">
                  <input
                    id="rules-grid-color"
                    type="color"
                    className="rules-php-color-input"
                    value={gridColor}
                    onChange={(e) => setGridColor(e.target.value)}
                  />
                </div>
              </div>

              <div className="rules-php-form-row">
                <label htmlFor="rules-grid-active-text-color" className="rules-php-label">
                  Grid Word Select/Active Text Color
                </label>
                <div className="rules-php-field">
                  <input
                    id="rules-grid-active-text-color"
                    type="color"
                    className="rules-php-color-input"
                    value={gridActiveTextColor}
                    onChange={(e) => setGridActiveTextColor(e.target.value)}
                  />
                </div>
              </div>

              <div className="rules-php-form-row">
                <label htmlFor="rules-word-btn-text-color" className="rules-php-label">
                  Find Word Button Text Color
                </label>
                <div className="rules-php-field">
                  <input
                    id="rules-word-btn-text-color"
                    type="color"
                    className="rules-php-color-input"
                    value={wordButtonTextColor}
                    onChange={(e) => setWordButtonTextColor(e.target.value)}
                  />
                </div>
              </div>

              <div className="rules-php-form-row">
                <label htmlFor="rules-word-btn-bg-color" className="rules-php-label">
                  Find Word Button Background Color
                </label>
                <div className="rules-php-field">
                  <input
                    id="rules-word-btn-bg-color"
                    type="color"
                    className="rules-php-color-input"
                    value={wordButtonBgColor}
                    onChange={(e) => setWordButtonBgColor(e.target.value)}
                  />
                </div>
              </div>

              <div className="rules-php-form-row">
                <label htmlFor="rules-word-grid-bg-color" className="rules-php-label">
                  Word Grid Background Color
                </label>
                <div className="rules-php-field">
                  <input
                    id="rules-word-grid-bg-color"
                    type="color"
                    className="rules-php-color-input"
                    value={wordGridBgColor}
                    onChange={(e) => setWordGridBgColor(e.target.value)}
                  />
                </div>
              </div>

              <div className="rules-php-form-row">
                <label htmlFor="rules-word-grid-text-color" className="rules-php-label">
                  Word Grid Text Color
                </label>
                <div className="rules-php-field">
                  <input
                    id="rules-word-grid-text-color"
                    type="color"
                    className="rules-php-color-input"
                    value={wordGridTextColor}
                    onChange={(e) => setWordGridTextColor(e.target.value)}
                  />
                </div>
              </div>

              <div className="rules-php-form-row rules-php-toggle-row">
                <span className="rules-php-label">Diagonal Mode</span>
                <div className="rules-php-field">
                  <label className="rules-php-switch">
                    <input
                      type="checkbox"
                      checked={rightDiagonal}
                      disabled={togglingDiagonal}
                      onChange={(e) => persistDiagonal(e.target.checked)}
                    />
                    <span className="rules-php-switch-slider" />
                  </label>
                </div>
              </div>

            </div>
          </div>
        </div>

        <div className="rules-php-footer">
          <button
            type="button"
            className="rules-php-next-btn"
            disabled={saving || !themeId}
            onClick={handleSubmit}
          >
            Next <i className="fa-solid fa-angle-double-right" />
          </button>
        </div>
      </div>
    </>
  );
};

export default Rules;
